/**
 * SharedAssessment — public, read-only view of an assessment summary
 * accessible via a magic-link share token. No login required.
 *
 *   Route: /shared/:token
 *
 * Renders a redacted Big-4-grade summary:
 *  - Headline level + score + completeness
 *  - Per-dimension radar (read-only)
 *  - Top 3 recommended moves
 *  - Sponsor message
 * Hides everything operational (indicator answers, evidence, pricing).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Lock, AlertTriangle, ArrowRight } from "lucide-react";
import axios from "axios";
import RadarMaturity from "../components/charts/RadarMaturity";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const LevelBadge = ({ level }) => {
  const map = {
    1: "bg-red-50 text-red-800 border-red-200",
    2: "bg-amber-50 text-amber-800 border-amber-200",
    3: "bg-blue-50 text-blue-800 border-blue-200",
    4: "bg-emerald-50 text-emerald-800 border-emerald-200",
    5: "bg-purple-50 text-[#7737F6] border-[#7737F6]/40",
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 border text-[12px] font-semibold tracking-[0.12em] uppercase ${map[level] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
      Level {level}
    </span>
  );
};

const SharedAssessment = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/public/maturity/shared/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This share link is invalid."));
  }, [token]);

  if (error) {
    return (
      <section className="min-h-[60vh] bg-white flex items-center justify-center px-4" data-testid="shared-error">
        <div className="max-w-md text-center">
          <AlertTriangle size={28} className="mx-auto text-amber-600" />
          <h1 className="ek-heading text-2xl text-black mt-4">{error}</h1>
          <p className="text-zinc-600 text-[14px] mt-3 font-light">If you think this is a mistake, ask the person who shared the link to issue a fresh one.</p>
          <Link to="/" className="text-[#7737F6] hover:underline text-[13px] mt-6 inline-block">Visit ekselens.com</Link>
        </div>
      </section>
    );
  }
  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="shared-loading">Loading…</div>;
  }

  const { share, summary } = data;
  const radarSeries = [{
    name: summary.client_label || "Your maturity",
    dimensions: (summary.dimensions || []).map((d) => ({ dim: d.label, score: d.weighted ?? 0 })),
  }];

  return (
    <section className="bg-white">
      <div className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-container relative z-10 py-16 md:py-20">
          <div className="flex items-center gap-2">
            <Lock size={13} className="text-[#A179FA]" />
            <div className="ek-eyebrow text-[#A179FA] text-[11px]">Shared with {share.recipient_name || "you"} · expires {fmtDate(share.expires_at)}</div>
          </div>
          <h1 className="ek-heading text-3xl md:text-4xl lg:text-5xl text-white mt-3 max-w-3xl" data-testid="shared-headline">
            {summary.sub_brand_label || summary.sub_brand} maturity summary
          </h1>
          <p className="text-zinc-300 text-[15px] mt-3 font-light">
            {summary.client_label}{summary.client_sector ? ` · ${summary.client_sector}` : ""}
          </p>
        </div>
      </div>

      <div className="ek-container max-w-5xl py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="border border-zinc-200 p-6">
            <div className="ek-eyebrow text-zinc-500 text-[10.5px]">Overall</div>
            <div className="mt-3 flex items-end gap-3">
              <div className="text-5xl ek-heading text-black tabular-nums" data-testid="shared-overall-score">{(summary.overall_weighted ?? 0).toFixed(1)}</div>
              <div className="text-zinc-500 text-[14px] pb-1">/ 5</div>
            </div>
            <div className="mt-3"><LevelBadge level={summary.overall_level} /></div>
            {summary.cap_applied ? (
              <div className="mt-3 text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1">Dealbreaker cap applied</div>
            ) : null}
          </div>
          <div className="border border-zinc-200 p-6">
            <div className="ek-eyebrow text-zinc-500 text-[10.5px]">Completeness</div>
            <div className="mt-3 text-5xl ek-heading text-black tabular-nums">{summary.completeness_pct ?? 0}%</div>
            <p className="text-zinc-600 text-[13px] mt-3 font-light">Proportion of indicators answered. The lower this is, the more cautiously the level interprets.</p>
          </div>
          <div className="border border-zinc-200 p-6">
            <div className="ek-eyebrow text-zinc-500 text-[10.5px]">Dimensions</div>
            <div className="mt-3 text-5xl ek-heading text-black tabular-nums">{(summary.dimensions || []).length}</div>
            <p className="text-zinc-600 text-[13px] mt-3 font-light">Independent scoring dimensions inside this framework.</p>
          </div>
        </div>

        {share.sponsor_message ? (
          <div className="mt-10 bg-[#FAF7FF] border-l-2 border-[#7737F6] p-6" data-testid="shared-sponsor-message">
            <div className="ek-eyebrow text-[#7737F6] text-[11px]">Message from Ekselens</div>
            <p className="mt-2 text-zinc-800 text-[15px] leading-relaxed font-light whitespace-pre-line">{share.sponsor_message}</p>
          </div>
        ) : null}

        <div className="mt-12">
          <h2 className="ek-heading text-2xl text-black">Maturity profile</h2>
          <div className="mt-6 max-w-2xl">
            <RadarMaturity series={radarSeries} />
          </div>
        </div>

        {summary.top_moves && summary.top_moves.length > 0 ? (
          <div className="mt-14">
            <h2 className="ek-heading text-2xl text-black">Top recommended moves</h2>
            <div className="mt-6 space-y-4">
              {summary.top_moves.map((m, i) => (
                <div key={m.id || i} className="border border-zinc-200 p-5" data-testid={`shared-move-${i}`}>
                  <div className="ek-eyebrow text-[#7737F6] text-[10.5px]">Move {String(i + 1).padStart(2, "0")} · {m.dimension}</div>
                  <div className="ek-heading text-lg text-black mt-2">{m.title}</div>
                  <div className="flex gap-3 mt-3 text-[12px] text-zinc-500">
                    {m.impact ? <span>Impact {m.impact}/5</span> : null}
                    {m.effort ? <span>Effort {m.effort}/5</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-16 pt-8 border-t border-zinc-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-zinc-500 text-[12.5px] font-light">This summary is shared for the named recipient only and excludes operational detail. To discuss in full, talk to your Ekselens lead.</p>
          <Link to="/contact" className="ek-btn ek-btn-primary inline-flex items-center gap-2 text-[13px] px-5 py-2.5" data-testid="shared-contact-cta">
            Book a discovery conversation <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SharedAssessment;
